import { Suspense } from "react";
import { useTranslations } from "use-intl";
import { toast } from "sonner";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  BookingSupplierConfirmationRecord,
  bookingSupplierConfirmationQuery,
} from "@/app/Bookings/GraphQL/bookingSupplierConfirmationQuery";
import {
  confirmBookingSupplierConfirmationMutation,
  dismissBookingSupplierConfirmationMutation,
} from "@/app/Bookings/GraphQL/bookingSupplierConfirmationMutations";
import {
  BookingStatus,
  BookingSupplierConfirmationCheckOutcome,
  BookingSupplierConfirmationStatus,
} from "@/gql/graphql";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { Button } from "@/components/catalyst/button";
import { Link } from "@/components/catalyst/link";
import { Text } from "@/components/catalyst/text";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { bookingStatusColorMap } from "@/app/Bookings/Utils/bookingStatusColorMap";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CheckIcon, DocumentTextIcon } from "@heroicons/react/20/solid";

const confirmableStatuses = [
  BookingStatus.AwaitingConfirmation,
  BookingStatus.Accepted,
];

const checkOutcomeColor: {
  [key in BookingSupplierConfirmationCheckOutcome]: BadgeColor;
} = {
  [BookingSupplierConfirmationCheckOutcome.Pass]: "green",
  [BookingSupplierConfirmationCheckOutcome.Warn]: "yellow",
  [BookingSupplierConfirmationCheckOutcome.Fail]: "red",
};

export function BookingSupplierConfirmationReviewDialog({
  confirmationId,
}: {
  confirmationId: string;
}) {
  return (
    <Suspense fallback={<div className="p-4 text-center">…</div>}>
      <ReviewContent confirmationId={confirmationId} />
    </Suspense>
  );
}

function ReviewContent({ confirmationId }: { confirmationId: string }) {
  const t = useTranslations("task.supplierConfirmationDialog");
  const tBooking = useTranslations("booking");
  const { close } = useDialogState();
  const { data } = useSuspenseGqlQuery(bookingSupplierConfirmationQuery, {
    id: confirmationId,
  });
  const confirm = useGqlMutation(confirmBookingSupplierConfirmationMutation);
  const dismiss = useGqlMutation(dismissBookingSupplierConfirmationMutation);

  const confirmation = data.bookingSupplierConfirmation;
  const booking = confirmation.booking;
  const isOpen =
    confirmation.status === BookingSupplierConfirmationStatus.AwaitingReview ||
    confirmation.status === BookingSupplierConfirmationStatus.Failed;
  const canConfirm =
    isOpen && booking !== null && confirmableStatuses.includes(booking.status);

  return (
    <BaseDialog
      title={t("title")}
      subtitle={t("subtitle", {
        supplierReference: confirmation.supplier_reference ?? "—",
        bookingReference: confirmation.booking_reference ?? "—",
      })}
      size="3xl"
    >
      <div className="space-y-6">
        <BookingSummary confirmation={confirmation} />

        {confirmation.failure_reason ? (
          <WarningBanner>
            {t("failure", { reason: confirmation.failure_reason })}
          </WarningBanner>
        ) : null}

        {booking === null ? (
          <WarningBanner>
            {t("noBooking", {
              reference: confirmation.booking_reference ?? "—",
            })}
          </WarningBanner>
        ) : null}

        {booking !== null && !confirmableStatuses.includes(booking.status) ? (
          <WarningBanner>
            {t("notConfirmable", {
              status: tBooking(`status.${booking.status}`),
            })}
          </WarningBanner>
        ) : null}

        {confirmation.checks.length > 0 ? (
          <CheckTable checks={confirmation.checks} />
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 pt-4">
          <div className="space-y-1">
            {confirmation.pdf ? (
              <a
                href={confirmation.pdf.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 underline"
              >
                <DocumentTextIcon className="size-4" />
                {t("viewPdf")}
              </a>
            ) : null}
            <Text className="text-xs text-gray-500">
              {t("viewEmail", { subject: confirmation.email_subject })}
            </Text>
          </div>

          {isOpen ? (
            <div className="flex gap-2">
              <Button
                plain
                isLoading={dismiss.isPending}
                disabled={confirm.isPending}
                onClick={async () => {
                  await dismiss.mutateAsync({ id: confirmation.id });
                  toast.success(t("dismissed"));
                  await close();
                }}
              >
                {t("dismiss")}
              </Button>
              <Button
                color="green"
                LeadingIcon={CheckIcon}
                isLoading={confirm.isPending}
                disabled={!canConfirm || dismiss.isPending}
                onClick={async () => {
                  await confirm.mutateAsync({ id: confirmation.id });
                  toast.success(t("confirmed"));
                  await close();
                }}
              >
                {t("confirm")}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </BaseDialog>
  );
}

function BookingSummary({
  confirmation,
}: {
  confirmation: BookingSupplierConfirmationRecord;
}) {
  const t = useTranslations("task.supplierConfirmationDialog");
  const tBooking = useTranslations("booking");
  const booking = confirmation.booking;

  if (booking === null) {
    return null;
  }

  return (
    <dl className="grid grid-cols-2 gap-4 rounded-md bg-gray-50 p-4 text-sm">
      <div>
        <dt className="text-gray-500">{t("booking")}</dt>
        <dd className="flex items-center gap-2">
          <Link
            className="text-blue-600 underline"
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to="/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"
            params={(prev) => ({ ...prev, bookingId: booking.id })}
            search={() => ({})}
          >
            {booking.reference}
          </Link>
          <span className="text-gray-700">{booking.name}</span>
          <Badge color={bookingStatusColorMap[booking.status]}>
            {tBooking(`status.${booking.status}`)}
          </Badge>
        </dd>
      </div>
      <div>
        <dt className="text-gray-500">{t("supplier")}</dt>
        <dd>{booking.relocation.supplier.name}</dd>
      </div>
    </dl>
  );
}

function CheckTable({
  checks,
}: {
  checks: BookingSupplierConfirmationRecord["checks"];
}) {
  const t = useTranslations("task.supplierConfirmationDialog");

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("thCheck")}</TableHead>
          <TableHead>{t("thBooking")}</TableHead>
          <TableHead>{t("thSupplier")}</TableHead>
          <TableHead>{t("thResult")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {checks.map((check) => (
          <TableRow key={check.type}>
            <TableCell className="font-medium">
              {t(`checkType.${check.type}`)}
            </TableCell>
            <TableCell className="whitespace-normal">
              {check.expected ?? "—"}
            </TableCell>
            <TableCell className="whitespace-normal">
              {check.actual ?? (
                <span className="text-gray-400">{t("notStated")}</span>
              )}
            </TableCell>
            <TableCell>
              <Badge color={checkOutcomeColor[check.outcome]}>
                {t(`checkOutcome.${check.outcome}`)}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
